// ============================================================
// TaskFlow OS - Project List Page
// ============================================================

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/layout/Header';
import GlassModal from '../components/ui/GlassModal';
import { useProjectStore } from '../stores/projectStore';
import { useAuthStore } from '../stores/authStore';
import type { Project } from '../lib/types';
import { IconProject, IconPlus, IconUsers, IconWorkflow, IconEdit, IconTrash, IconChevronRight, IconEye } from '../icons/SvgIcons';

const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { projects, workflows, fetchProjects, createProject, deleteProject, fetchWorkflows } = useProjectStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newVisibility, setNewVisibility] = useState<'public' | 'team' | 'private'>('team');
  const [newColor, setNewColor] = useState('#10b981');

  useEffect(() => {
    // In a real app, you'd have the org ID from context
    // For now, fetch all accessible projects
    if (user) {
      fetchProjects('default-org');
    }
  }, [user]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const result = await createProject({
      organization_id: 'default-org',
      name: newName,
      description: newDesc || undefined,
      visibility: newVisibility,
      color: newColor,
      owner_id: user?.id,
    });

    if (!result.error) {
      setNewName('');
      setNewDesc('');
      setShowCreateModal(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('このプロジェクトをアーカイブしますか？')) {
      await deleteProject(id);
    }
  };

  const VISIBILITY_LABELS = { public: '公開', team: 'チーム', private: '非公開' };

  return (
    <>
      <Header
        title="プロジェクト一覧"
        subtitle={`${projects.length} 件のプロジェクト`}
        actions={
          <button onClick={() => setShowCreateModal(true)} className="glass-btn-primary flex items-center gap-2 text-sm">
            <IconPlus size={14} /> 新規作成
          </button>
        }
      />

      <div className="p-6">
        {projects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-16 h-16 mx-auto rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
              <IconProject size={28} className="text-white/20" />
            </div>
            <p className="text-white/40 mb-4">プロジェクトがまだありません</p>
            <button onClick={() => setShowCreateModal(true)} className="glass-btn-primary text-sm">
              最初のプロジェクトを作成
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {projects.map((project, idx) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="glass-card group cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                {/* Color strip */}
                <div className="h-1 -mt-5 -mx-5 mb-4 rounded-t-2xl" style={{ background: project.color }} />

                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: `${project.color}15`, border: `1px solid ${project.color}25` }}
                    >
                      <IconProject size={18} color={project.color} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-white truncate">{project.name}</h3>
                      <span className="text-[10px] text-white/30">{VISIBILITY_LABELS[project.visibility]}</span>
                    </div>
                  </div>

                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); }}
                      className="p-1.5 rounded-lg hover:bg-white/10"
                    >
                      <IconEdit size={13} className="text-white/40" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}
                      className="p-1.5 rounded-lg hover:bg-red-500/10"
                    >
                      <IconTrash size={13} className="text-red-400/40" />
                    </button>
                  </div>
                </div>

                {project.description && (
                  <p className="text-xs text-white/40 mb-3 line-clamp-2">{project.description}</p>
                )}

                <div className="flex items-center justify-between text-[10px] text-white/30">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <IconUsers size={11} /> {(project.members || []).length}
                    </span>
                    <span className="flex items-center gap-1">
                      <IconWorkflow size={11} /> 0
                    </span>
                  </div>
                  <span>{new Date(project.created_at).toLocaleDateString('ja-JP')}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <GlassModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="新規プロジェクト"
        subtitle="プロジェクトを作成してチームで管理しましょう"
        footer={
          <>
            <button onClick={() => setShowCreateModal(false)} className="glass-btn text-sm">キャンセル</button>
            <button onClick={handleCreate} className="glass-btn-primary text-sm">作成</button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5">プロジェクト名</label>
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} className="glass-input" placeholder="例: ウェブサイトリニューアル" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">説明</label>
            <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} className="glass-textarea" placeholder="プロジェクトの概要（任意）" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">公開範囲</label>
            <select value={newVisibility} onChange={(e) => setNewVisibility(e.target.value as any)} className="glass-select">
              <option value="public">公開 - 全員が閲覧可能</option>
              <option value="team">チーム - 組織メンバーのみ</option>
              <option value="private">非公開 - 招待メンバーのみ</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">テーマカラー</label>
            <div className="flex gap-2">
              {['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4'].map((c) => (
                <button
                  key={c}
                  onClick={() => setNewColor(c)}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${newColor === c ? 'scale-110 border-white/40' : 'border-transparent'}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
        </div>
      </GlassModal>
    </>
  );
};

export default ProjectList;
